<template lang='pug'>
.picker.swiper-no-swiping
	.loading-screen
		.loading-indicator Loading
			span .
			span .
			span .
	.interface
		short-text-input(
			v-model='searchQuery'
			:class='{ loading : !allowSearch }'
			:options='textInputOptions'
			:errors='getError( "description" )'
		)

		.controls
			.control-button.highlight-background.plus(@click='changeZoom( 1 )')
			.control-button.highlight-background.minus(
				@click='changeZoom( -1 )'
				:class='{ disabled : zoom === 0 }'
			)

		p.spacey(
			:class='{ error : noLocation }'
		) click the map to drop a pin
	viewer(
		:location='location'
		:zoom='zoom'
		:pin='pin'

		@ready='initializeMap'
		@click='updateLocation'
		@zoom='zoom = $event'
		@remove-pin='removePin'
	)
</template>

<script>
import gmapsInit from '@/lib/gmaps';
import Viewer from '@/components/map/Viewer';
import ShortTextInput from '@/components/form-inputs/ShortTextInput';
import { Compare, HasProperty } from '@/lib/utils';

export default {
	name : 'map-picker',

	props : {
		value : {
			type : Object,
		},

		options : {
			type    : Object,
			default : () => ( {
				placeholder : 'e.g. "First and Herndon",'
			} ),
		},

		searchTimeoutDuration : {
			type    : Number,
			default : () => 500,
		},

		errors : {
			type : [Object, Number],
		}
	},

	data : () => ( {
		client      : null,
		searchQuery : '',
		allowSearch : false,
		google      : null,

		searchTimeout : null,

		placesService : null,

		location : {
			lat : 36.746081,
			lng : -119.789243,
		},
		zoom : 12,

		noLocation : false,

		pin : null,

		startedWithValue   : false,
		useInitialValue    : false,
		initialSearchQuery : '',
	} ),

	async created() {
		this.startedWithValue = this.value.locationsKey !== null;
		this.useInitialValue  = this.startedWithValue;

		if ( this.startedWithValue ) {
			const {
				description,
				longitude : lng,
				latitude  : lat,
			} = this.value;

			this.location = {
				lat,
				lng,
			};

			this.pin = {
				lat,
				lng,
			};

			this.initialSearchQuery = description;
			this.searchQuery        = description;
		}

		this.google = await gmapsInit();
	},

	computed : {

		textInputOptions() {

			return {
				label       : this.options.label || 'Cross Streets',
				placeholder : this.allowSearch ? this.options.placeholder : 'Loading...',
			};
		},

		localValue() {
			const { searchQuery } = this;
			const pin             = this.pin || {};
			const { lng, lat }    = pin;

			const loc = {
				description 	: searchQuery,
				longitude   	: lng || '',
				latitude    	: lat || '',
				locationsKey : null,
			};

			if ( this.startedWithValue && this.useInitialValue ) {
				const { locationsKey } = this.value;

				loc.locationsKey = locationsKey;
			}

			return loc;
		}

	},

	methods : {

		removePin() {
			this.pin = null;
		},

		initializeMap( map ) {
			this.placesService = new this.google.maps.places.PlacesService( map );
			this.map           = map;
			this.allowSearch   = true;
		},

		updateLocation( e ) {
			this.useInitialValue = false;

			const { location } = e;

			// this.location = location;
			this.pin        = location;
			this.noLocation = false;
		},

		changeZoom( dir ) {

			if ( dir < 0 && this.zoom === 0 ) {
				return;
			}

			this.zoom += dir;

		},

		getError( key ) {
			if ( this.errors === true ) {
				return true;
			}

			if ( HasProperty( this.errors, key ) ) {
				return this.errors[key] || true;
			}

			return true;
		}

	},

	watch : {

		localValue : {
			deep : true,
			handler( v ) {
				if ( !Compare( v, this.value ) ) {
					this.$emit( 'input', v );
				}
			}
		},

		searchQuery( query ) {

			if ( !this.allowSearch || !window.google ) {
				// console.log( 'Not ready to search' );

				return;
			}

			if ( this.startedWithValue && this.initialSearchQuery !== query ) {
				this.useInitialValue = false;
			}

			if ( this.searchTimeout ) {
				window.clearTimeout( this.searchTimeout );
			}

			this.searchTimeout = window.setTimeout( () => {

				this.placesService.findPlaceFromQuery( {
					query,
					fields : ['name', 'geometry']
				}, ( results ) => {
					// set an error message if there are no
					// results
					if ( !results ) {
						this.errorMessage = `No results for "${query}"`;

						return;
					}

					// determine the center
					const { location } = results[0].geometry;
					const { lat, lng } = location;
					// eslint-disable-next-line no-unused-vars
					const latLng       = { lat : lat(), lng : lng() };
					const center       = new this.google.maps.LatLng( lat(), lng() );

					// if they have already dropped a pin, it
					// is likely that they wouldn't like to move
					// the pin.
					// ! Deprecated ! //
					// if ( this.pin !== null && !this.options.multiplePins ) {
					// 	return;
					// }

					// if they haven't dropped a pin, we can drop it now
					// ! Deprecated ! //
					// this.pin = latLng;
					this.map.panTo( center );
					this.map.setZoom( 15 );
				} );

			}, this.searchTimeoutDuration );

		},

		// eslint-disable-next-line no-unused-vars
		errors( errors ) {
			this.noLocation = this.getError( 'longitude' ) !== true;
		}

	},

	components : {
		Viewer,
		ShortTextInput,
	}
};
</script>

<style lang='scss'>
.picker {
	width: 100%;
	border-radius: 15px;
	overflow: hidden;
	position: relative;

	&.wide-search-bar {

		.interface .short-text {
			width: 100%;
		}
	}

	.loading-screen {
		position: absolute;
		height: 100%;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: $map-bg;
	}

	.interface {
		position: absolute;
		height: 100%;
		width: 100%;
		padding: 15px;
		z-index: 2;
		pointer-events: none;

		* {
			pointer-events: all;
		}

		p.spacey {
			text-align: center;
			position: absolute;
			left: 50%;
			bottom: 15px;
			transform: translateX(-50%);

			&.error {
				color: $error;
			}
		}

		.controls {
			position: absolute;
			bottom: 15px;
			left: 15px;

			.control-button {
				height: 36px;
				width: 36px;
				border-radius: 50%;
				background: $color-primary;
				box-shadow: $inputBoxShadow;
				position: relative;
				cursor: pointer;
				transition: transform 0.2s ease;

				&:focus {
					transform: scale(0.95,0.95);
				}

				&.disabled {
					filter: grayscale(1);
				}

				+.control-button {
					margin-top: 12px;
				}

				&::before,
				&::after {
					content : ' ';
					height: 3px;
					width: 18px;
					border-radius: 2px;
					background: white;
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%,-50%);
				}

				&.plus {

					&::after {
						transform: translate(-50%,-50%) rotate(90deg);
					}
				}
			}
		}

		.short-text {
			background: white;
			width: 90%;

			input {
				text-align: left;
				margin-left: 10px;
			}
		}
	}
}
</style>
